//libs
import React, { useState, memo, MouseEvent, useCallback } from 'react';
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { Theme } from '@nykaa/ui-components/themes/types';

// constants
import { BUTTON, CAROUSEL_ANIMATION_CLASSNAME, DOTS_WIDTH_CONSTANT } from './constants';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { PLP_EVENTS } from 'shared/helpers/analytics/constants/plpEvents';

//styles
import { Image, DotsContainer, ArrowIconButton, ArrowButtons, Dots } from './styles';

//icons
import { useStoreIcon, IconName } from 'shared/icons';

//types
import { CAROUSEL_STATES } from 'shared/components/ProductV2/types';

//helpers
import { memoImageSwipe } from 'shared/layouts/Listing/helpers/impressions';

//analytics
import NFA from 'shared/helpers/analytics/NFAnalytics';

interface IPLPPDPImage {
  id: string;
  aspect_ratio: string;
  mediaType: string;
  position: string;
  url: string;
}

interface CarouselTrackingData {
  productID: string;
  productName: string;
  productBrandName: string;
  MRP: number;
  discountedPrice: number;
  productTags: Listing.IProductTag[] | undefined;
}

interface CarouselProps {
  images: IPLPPDPImage[];
  isImageLazy?: boolean;
  aspectRatio: number;
  width: number;
  className: string;
  ImageChildren: React.ReactNode;
  carouselState: CAROUSEL_STATES;
  setCarouselState: (carouselState: CAROUSEL_STATES) => void;
  alt: string;
  productId: string;
  carouselTrackingData?: CarouselTrackingData;
}

const ImageCarousel = ({
  images = [],
  isImageLazy = false,
  aspectRatio,
  width,
  className = '',
  alt,
  carouselState,
  setCarouselState,
  productId,
  carouselTrackingData,
}: CarouselProps) => {
  const ChevronRight = useStoreIcon(IconName.CHEVRON_RIGHT_SMALL);
  const theme: Theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCarouselFirstScroll, setIsCarouselFirstScroll] = useState(true);

  const trackFirstImageSwiped = () => {
    setIsCarouselFirstScroll(false);
    NFA.track({
      event: PLP_EVENTS.CAROUSEL_FIRST_IMAGE_SWIPED,
      type: TRACK_TYPE.EVENT,
      data: carouselTrackingData,
    });
  };

  const updateIndex = useCallback(
    (index: number) => {
      memoImageSwipe(productId, index);
      if (index === images.length) {
        setCurrentIndex(0);
        return;
      }

      setCurrentIndex(index);
    },
    [images.length, productId]
  );

  const prevArrowClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (currentIndex === 0) {
      updateIndex(images.length - 1);
      return;
    }
    updateIndex(currentIndex - 1);
    if (carouselState !== CAROUSEL_STATES.FOCUS) setCarouselState(CAROUSEL_STATES.FOCUS);
    if (isCarouselFirstScroll && carouselTrackingData) trackFirstImageSwiped();
  };

  const nextArrowClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (currentIndex === images.length - 1) {
      updateIndex(0);
      return;
    }

    updateIndex(currentIndex + 1);
    if (carouselState !== CAROUSEL_STATES.FOCUS) setCarouselState(CAROUSEL_STATES.FOCUS);
    if (isCarouselFirstScroll && carouselTrackingData) trackFirstImageSwiped();
  };

  const animationStart = () => updateIndex(currentIndex + 1);

  return (
    <div className={className} data-test="carousel-container">
      <div>
        {images.map((img, index) => {
          const classname =
            index === currentIndex && carouselState === CAROUSEL_STATES.AUTOPLAY
              ? CAROUSEL_ANIMATION_CLASSNAME
              : '';

          if (index > 0 && __SERVER__) {
            return null;
          }

          return (
            <Image
              onAnimationStart={animationStart}
              width={width}
              className={classname}
              aspectRatio={aspectRatio}
              key={`PLP-image-${index}`}
              src={img.url}
              lazy={isImageLazy}
              alt={`${alt} - ${index}`}
              stackTop={index === currentIndex}
              data-test="product-img"
            />
          );
        })}
      </div>
      <DotsContainer className="carousel-dots">
        <Dots totalCount={images.length} activeIndex={currentIndex} width={DOTS_WIDTH_CONSTANT} />
      </DotsContainer>

      <ArrowButtons className="carousel-arrow">
        <ArrowIconButton
          buttonType={BUTTON.PREV}
          onClick={prevArrowClick}
          kind="secondary"
          shape="default"
          size="small"
          data-test="left-arrow"
        >
          <ChevronRight color='#1B1E21' />
        </ArrowIconButton>
        <ArrowIconButton
          buttonType={BUTTON.NEXT}
          onClick={nextArrowClick}
          kind="secondary"
          shape="default"
          size="small"
          data-test="right-arrow"
        >
          <ChevronRight color='#1B1E21' />
        </ArrowIconButton>
      </ArrowButtons>
    </div>
  );
};

export default memo(ImageCarousel);
